import type { RoutePath } from '#routes.ts';
import type { Theme } from '#states/UIState.js';

import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { ROUTES } from '@elara/client-core/constants';
import { cn } from '@elara/client-core/lib/utils';

import { isFeedbackAvailable, openFeedbackDialog } from '#lib/sentry-feedback.js';
import {
  useAppState,
  useAppStates,
  useCurrentUserProfile,
  useMembershipPaymentState,
  useProductTourState,
  useSessionState,
  useTripsState,
  useUIState,
  useUserProfileState,
} from '#states/hooks.js';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  IconSizes,
  Logo,
  MaterialIcon,
} from '#ui/index.js';
import { DebugInspector } from '#views/chat/DebugInspector.jsx';
import { RouteErrorFallback } from '#views/error/RouteErrorFallback.js';
import { NotificationsBell } from '#views/layout/NotificationsBell.js';
import { ReferralDialog } from '#views/layout/ReferralDialog.jsx';
import { TrialRing } from '#views/layout/TrialRing.js';
import { PageTransition } from '#views/PageTransition.jsx';
import { PreviewPanel } from '#views/preview/PreviewPanel.js';
import { Link, LinkButton } from '#views/Router.js';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: React.ReactNode;
  backButtonRoute?: RoutePath;
  pageActions?: React.ReactNode;
  transitionKey?: string;
  headerContent?: React.ReactNode;
  allowScroll?: boolean;
  /** When true, hides the sidebar completely (used during onboarding). */
  hideSidebar?: boolean;
}

const ADMIN_PATH = '/admin/';

function getInitials(displayName: string | undefined, email: string | undefined): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
    }
    if (parts.length === 1) {
      return parts[0]!.slice(0, 2).toUpperCase();
    }
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return 'U';
}

function DebugSidebar() {
  const appState = useAppState();
  const { debugInspectorState } = useAppStates('debugInspectorState');
  return (
    <div className="h-full border-l bg-background flex flex-col">
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center  flex-row gap-2">
          <h3 className="font-semibold text-sm">Debug Inspector</h3>
          <Button variant="ghost" size="icon" className="h-6 w-4" onClick={async () => debugInspectorState.refresh()}>
            <MaterialIcon icon="refresh" size={IconSizes.sm} />
          </Button>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => appState.toggleDebugMode()}>
          <MaterialIcon icon="close" size={IconSizes.sm} />
        </Button>
      </div>
      <div className="flex-1 overflow-hidden">
        <DebugInspector />
      </div>
    </div>
  );
}

export function UserProfileDropdown({ displayLabel }: { displayLabel: boolean }) {
  const profile = useCurrentUserProfile();
  const userProfileState = useUserProfileState();
  const sessionState = useSessionState();
  const uiState = useUIState();
  const appState = useAppState();
  const tripsState = useTripsState();
  const productTourState = useProductTourState();
  const feedbackAvailable = isFeedbackAvailable();
  // Only while a free trial is running (ELARA-1042): the ring winds down around the avatar and the
  // menu below states the days in words. Both read the shared `MembershipPaymentState`. A failed
  // membership payment draws a full red ring instead (the two cannot overlap: trialing vs past_due).
  const membershipPaymentState = useMembershipPaymentState();
  const trial = membershipPaymentState.trial;
  const hasFailedPayment = membershipPaymentState.hasFailedPayment;
  const hasRing = hasFailedPayment || trial !== undefined;

  if (!sessionState.user) return;

  const displayName = profile?.displayName || sessionState.user.displayName || 'User';
  const email = profile?.email || sessionState.user.email || '';
  const photoURL = profile?.photoURL || sessionState.user.photoURL || undefined;
  const initials = profile?.initials || getInitials(displayName, email);

  const themeChanged = (value: string) => {
    const theme = value as Theme;
    // Save to profile and apply immediately
    void userProfileState.setTheme(theme);
    uiState.applyTheme(theme);
  };

  const isSidebarOpen = displayLabel && uiState.isSidebarOpen;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={isSidebarOpen ? 'sidebar' : 'icon'}
          size={isSidebarOpen ? 'sidebar' : 'icon'}
          className={cn('justify-start', isSidebarOpen && 'pl-0 py-0 rounded-l-[20px]', 'md:w-full z-10')}
        >
          {/* Its own 40pt box rather than the button's, which stretches to the sidebar's full width
              once expanded and would draw the ring as an ellipse around the whole row. */}
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center">
            {hasFailedPayment ? (
              <TrialRing fractionRemaining={1} tone="destructive" />
            ) : (
              trial && <TrialRing fractionRemaining={trial.fractionRemaining} />
            )}
            {/* Tucked inside the ring when there is one, so the two do not touch. */}
            <Avatar className={cn(hasRing && 'h-8 w-8')}>
              <AvatarImage src={photoURL} alt={displayName} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </span>
          {displayLabel && (
            <span
              className={cn(
                'absolute left-10 top-0 bottom-0 flex items-center overflow-hidden pointer-events-none',
                !isSidebarOpen && 'w-0',
              )}
            >
              <span
                className={cn(
                  'pl-2 whitespace-nowrap transition-opacity duration-200 ease-out',
                  isSidebarOpen ? 'opacity-100' : 'opacity-0',
                )}
              >
                {profile?.displayName}
              </span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" side={tripsState.isMobile ? 'bottom' : 'right'} forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">{email}</p>
            {/* Green, matching the ring it explains (ELARA-1042) — and green to the last day, so a
                trial reads as a benefit in use rather than a fault to fix. */}
            {trial && (
              <p className="text-xs font-medium leading-none mt-1 text-green-600 dark:text-green-500">{trial.label}</p>
            )}
            {/* Red, matching the ring it explains; the trips and account pages carry the fix. */}
            {hasFailedPayment && (
              <p className="text-xs font-medium leading-none mt-1 text-destructive">Membership overdue</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={ROUTES.ACCOUNT} className="cursor-pointer hover:bg-accent hover:text-accent-foreground">
            <MaterialIcon icon="settings" className="mr-2" />
            <span>Account</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTimeout(() => void userProfileState.openReferralDialog(), 50)}>
          <MaterialIcon icon="share" className="mr-2" />
          <span>Refer a Friend</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setTimeout(async () => productTourState.start(), 50);
          }}
          className="flex items-center gap-2"
        >
          <MaterialIcon icon="tour" />
          <span>Take the tour</span>
        </DropdownMenuItem>

        {userProfileState.canAccessAdmin && (
          <DropdownMenuItem asChild>
            <a href={ADMIN_PATH} className="cursor-pointer hover:bg-accent hover:text-accent-foreground">
              <MaterialIcon icon="admin_panel_settings" className="mr-2" />
              <span>Admin Console</span>
            </a>
          </DropdownMenuItem>
        )}

        {userProfileState.isPrivileged && (
          <DropdownMenuItem onClick={() => appState.toggleDebugMode()}>
            <MaterialIcon icon="bug_report" className="mr-2" />
            <span>Inspect</span>
          </DropdownMenuItem>
        )}

        {feedbackAvailable && (
          <DropdownMenuItem onSelect={() => void openFeedbackDialog()}>
            <MaterialIcon icon="info" className="mr-2" />
            <span>Report Feedback</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <MaterialIcon icon="routine" className="mr-2" />
            <span>Theme</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={profile?.theme || 'system'} onValueChange={themeChanged}>
                <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        {/* <DropdownMenuItem>
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem> */}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={async () => sessionState.logout()}>
          <MaterialIcon icon="logout" className="mr-2" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppLayoutTitle({ title }: { title: string }) {
  return <h1 className="font-medium text-sm truncate min-w-0 ml-2">{title}</h1>;
}

export function AppLayout({
  children,
  backButtonRoute: backButtonUrl,
  pageActions,
  title,
  transitionKey,
  headerContent,
  allowScroll = true,
  hideSidebar = false,
}: AppLayoutProps) {
  const { appState, sessionState, userProfileState, uiState, tripsState } = useAppStates(
    'appState',
    'sessionState',
    'userProfileState',
    'uiState',
    'tripsState',
  );

  /**
   * The expanded sidebar is a `md:` concept — below that breakpoint the header is a horizontal strip
   * with no column to expand into, and every width class that would honour this is `md:`-prefixed.
   *
   * So it has to be false on a narrow viewport, or the bell and the avatar render in their `sidebar`
   * variant there: wider buttons carrying absolutely-positioned labels that spill sideways across
   * the strip and push the avatar off its right edge. That was always the case; the labels simply
   * landed behind a full-width avatar and were invisible until the trial ring (ELARA-1042) shrank it.
   */
  const isSidebarOpen = headerContent && uiState.isSidebarOpen && !tripsState.isMobile;

  return (
    <PanelGroup id="dashboard" direction="horizontal" className="h-full">
      <Panel
        id="main-panel"
        defaultSize={appState.debugMode && userProfileState.isPrivileged ? 75 : 100}
        minSize={50}
        order={1}
        className={cn(
          'relative flex flex-col md:flex-row h-full transition-background duration-500 bg-background-secondary safe-area-top',
          allowScroll ? 'h-screen' : 'h-dvh',
        )}
      >
        {/* Sidebar */}
        {!hideSidebar && (
          <header
            className={cn(
              'flex items-start justify-between gap-4 px-3 py-2 z-30',
              'md:px-2 md:py-4 border-r w-full',
              'md:relative md:flex-col md:top-auto md:left-auto md:right-auto',
              headerContent &&
                'overflow-hidden transition-[width] duration-250 ease-out will-change-[width] md:isolate',
              headerContent && (isSidebarOpen ? 'md:w-50' : 'md:w-14'),
              !headerContent && 'md:w-auto',
            )}
          >
            <div className="flex md:flex-col items-center md:items-start flex-1 gap-2 md:gap-8 min-w-0 flex-shrink z-10 w-full">
              <div className={cn('flex md:flex-col items-center gap-2 md:gap-4 md:w-full')}>
                <Link
                  to={ROUTES.HOME}
                  className={cn(
                    'cursor-pointer md:w-full bg-background-secondary md:bg-transparent hover:bg-secondary transition-opacity flex-shrink-0 p-1 rounded-sm',
                    isSidebarOpen ? 'rounded-l-[20px]' : 'rounded-full',
                  )}
                >
                  <Logo variant={isSidebarOpen ? 'full' : 'symbol'} className="hidden md:inline-block" />
                  <Logo variant="symbol" className="md:hidden" />
                </Link>

                {backButtonUrl && (
                  <LinkButton to={backButtonUrl} variant="icon" size="icon">
                    <MaterialIcon icon="arrow_back" />
                  </LinkButton>
                )}
              </div>

              {headerContent}
            </div>

            <div className={cn('flex flex-col shrink-0 items-start gap-4 md:w-full z-10')}>
              {headerContent && (
                <Button
                  variant={isSidebarOpen ? 'sidebar' : 'icon'}
                  size={isSidebarOpen ? 'sidebar' : 'icon'}
                  onClick={() => {
                    uiState.toggleSidebar();
                    void userProfileState.setSidebarOpen(uiState.isSidebarOpen);
                  }}
                  className={cn('hidden md:flex', isSidebarOpen && 'w-full')}
                  tooltip={!isSidebarOpen ? 'Show sidebar' : undefined}
                  tooltipPosition="right"
                >
                  <MaterialIcon icon={isSidebarOpen ? 'left_panel_close' : 'left_panel_open'} />
                  <span
                    className={cn(
                      'absolute left-10 top-0 bottom-0 flex items-center overflow-hidden pointer-events-none',
                      !isSidebarOpen && 'w-0',
                    )}
                  >
                    <span
                      className={cn(
                        'pl-2 whitespace-nowrap transition-opacity duration-200 ease-out',
                        isSidebarOpen ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      Hide sidebar
                    </span>
                  </span>
                </Button>
              )}

              {userProfileState.profile && sessionState.user && (
                <div className="flex flex-row md:flex-col items-center md:items-start gap-2 md:gap-4 md:w-full">
                  <NotificationsBell displayLabel={!!isSidebarOpen} />

                  <UserProfileDropdown displayLabel={!!isSidebarOpen} />
                  <ReferralDialog />
                </div>
              )}
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className="relative flex-1 overflow-hidden">
          {/* Chat Interface Panel */}
          <ErrorBoundary FallbackComponent={RouteErrorFallback}>
            {transitionKey ? <PageTransition transitionKey={transitionKey}>{children}</PageTransition> : children}
          </ErrorBoundary>

          {/* Guide preview panel — global so hotel/place/destination guides open on any page (Stories,
              etc.), not just the trip page. Sibling of the page so it isn't replayed by PageTransition.
              It's an absolute right-edge overlay anchored to this <main>; open/close is driven by the
              `preview`/`id` URL hash via tripsState.handleRouteChange. */}
          <PreviewPanel isOpen={tripsState.isPreviewPanelOpen} onClose={() => tripsState.closePreviewPanel()} />
        </main>
      </Panel>

      {/* Debug Sidebar Panel */}
      {appState.debugMode && userProfileState.isPrivileged && (
        <>
          <PanelResizeHandle className="w-0.25 bg-border hover:bg-primary/20 transition-colors" />
          <Panel
            id="right-panel"
            className={cn(allowScroll ? 'h-screen' : 'h-dvh')}
            defaultSize={25}
            minSize={15}
            maxSize={50}
            order={2}
          >
            <DebugSidebar />
          </Panel>
        </>
      )}
    </PanelGroup>
  );
}
